import {
  OrderPickupLocation,
  OrderPickupPerson,
} from 'typescript/types/api/solidshop';
import t from 'typescript/utilities/translate';

// see docs on JS Intl.DateTimeFormat() constructor
type FormatOptionsObject = {
  hour: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
};

// e.g. '1.32 mi'
export const distanceToStore = (distanceInMiles: number) =>
  t('checkout.delivery.pickup.distanceInMiles', {
    num: Math.round(distanceInMiles * 100) / 100,
  });

// e.g. 'Best Buy, Jersey City'
export const fullLocationName = (pickupLocation: OrderPickupLocation) =>
  t('checkout.delivery.condensed.retailerLocation', {
    partnerStore: pickupLocation.partner_store,
    locationName: pickupLocation.location_name,
  });

// e.g. '123 Fake St., New York, NY 10012'
export const pickupLocationAddress = (pickupLocation: OrderPickupLocation) =>
  t('checkout.delivery.condensed.address', {
    addressLine1: pickupLocation.address_line_1,
    city: pickupLocation.city,
    state: pickupLocation.state,
    postalCode: pickupLocation.postal_code,
  });

// e.g. 'John Smith, jsmith@example.com, (310) 555-5555'
export const pickupPersonInfo = (pickupPerson: OrderPickupPerson) =>
  t('checkout.delivery.condensed.contactInfo', {
    firstName: pickupPerson.first_name,
    lastName: pickupPerson.last_name,
    email: pickupPerson.email,
    phone: pickupPerson.phone,
  });

// e.g. 'Open until 10 pm' or 'Open until 8:30 pm'
export const openUntilStr = (openUntilTime: string) => {
  const dateTime = new Date(openUntilTime);
  const options: FormatOptionsObject = { hour: 'numeric' };

  if (dateTime.getMinutes() !== 0) {
    options.minute = '2-digit';
  }

  const closingTime = new Intl.DateTimeFormat(undefined, options)
    .format(dateTime)
    .toLocaleLowerCase();

  return t('checkout.delivery.pickup.openUntil', { closingTime });
};

// e.g. 'Wednesday, May 21'
export const weekdayAndDate = (date: string, timeZone: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));

// e.g. 'Ready today', 'Ready tomorrow', or 'Ready Wednesday, May 21'
export const readyDateStr = (
  daysUntilAvailable: number,
  availabilityDate: string,
  timeZone: string,
) => {
  let day;

  switch (daysUntilAvailable) {
    case 0: {
      day = t('checkout.delivery.pickup.today');
      break;
    }
    case 1: {
      day = t('checkout.delivery.pickup.tomorrow');
      break;
    }
    default: {
      day = weekdayAndDate(availabilityDate, timeZone);
    }
  }

  return t('checkout.delivery.pickup.readyDate', { date: day });
};

// e.g. 'Ready in 90 minutes' or 'Ready in 2 hours'
export const durationStr = (readyInMinutes: number) => {
  let duration: string;
  let hours: number;

  if (!readyInMinutes) return null;

  // e.g. 30 minutes, 90 minutes, but not 60 minutes
  if (readyInMinutes !== 60 && readyInMinutes <= 90) {
    duration = t('checkout.delivery.pickup.xMinutes', { x: readyInMinutes });
  } else {
    hours = readyInMinutes / 60;
    // if ready in 150 minutes, let's round up to 3 hours
    if (readyInMinutes % 60 > 0) hours += 1;
    duration = t('checkout.delivery.pickup.xHours', { x: hours });
  }

  return t('checkout.delivery.pickup.readyInDuration', { duration });
};
